import { useEffect, useMemo, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';

/**
 * Site-wide promo banner — deliberately not another glass card. Ticket / boarding-pass
 * silhouette with dashed side-notch perforation, rotating conic aura, shimmer sweep,
 * and a live countdown pill. Same component powers Pricing (top banner) and the
 * signed-in Account dashboard, so the promo reads as one recognisable object across
 * the site — not two different cards saying vaguely the same thing.
 *
 * Fully responsive: stacks vertical below `sm`, code + countdown wrap first,
 * perforation dots reduce, and reduced-motion collapses the aura + shimmer.
 */
export type PromoLike = {
  code: string;
  reward_type?: string;
  reward_value?: number | string;
  title?: string;
  description?: string;
  expires_at?: string;
};

export type PromoBannerVariant = 'wide' | 'card';

export default function PromoBanner({
  promo,
  variant = 'wide',
  className = '',
}: {
  promo: PromoLike;
  variant?: PromoBannerVariant;
  className?: string;
}) {
  const reduce = useReducedMotion();
  const pct = Number(promo.reward_value) || 0;
  const [copied, setCopied] = useState(false);
  const [remaining, setRemaining] = useState(() => timeLeft(promo.expires_at));

  useEffect(() => {
    if (!promo.expires_at) return;
    setRemaining(timeLeft(promo.expires_at));
    const id = setInterval(() => setRemaining(timeLeft(promo.expires_at)), 60_000);
    return () => clearInterval(id);
  }, [promo.expires_at]);

  const label = useMemo(() => {
    if (!remaining) return null;
    const { d, h, m } = remaining;
    if (d > 0) return `${d}d ${h}h left`;
    if (h > 0) return `${h}h ${m}m left`;
    return `${m}m left`;
  }, [remaining]);

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(promo.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {}
  };

  return (
    <div className={`promo-ticket ${variant === 'card' ? 'promo-ticket--card' : ''} ${className}`}>
      {/* Rotating conic aura — decorative */}
      {!reduce && (
        <motion.span
          aria-hidden
          className="promo-aura"
          animate={{ rotate: 360 }}
          transition={{ duration: 22, ease: 'linear', repeat: Infinity }}
        />
      )}
      {/* Perforation columns */}
      <span aria-hidden className="promo-perf promo-perf--left" />
      <span aria-hidden className="promo-perf promo-perf--right" />

      <div className="promo-body">
        {/* Left — stub */}
        <div className="promo-stub">
          <div className="promo-stub__pct">
            <span className="h-title">−{pct}</span>
            <span className="promo-stub__sign">%</span>
          </div>
          <div className="promo-stub__label">off every plan</div>
        </div>

        {/* Middle — headline + description + code */}
        <div className="promo-main">
          <div className="promo-badges">
            <span className="chip chip-ok">Live promo</span>
            {label && <span className="promo-countdown"><span className="promo-countdown__dot" />{label}</span>}
          </div>

          <h3 className="promo-title h-title">
            <span className="promo-shimmer">
              {promo.title || `${pct}% off every Vpnee plan`}
            </span>
          </h3>

          <p className="promo-copy">
            {promo.description || 'Auto-applied at checkout — no maths, no fine print.'}
            {promo.expires_at ? (
              <> · Ends <strong>{String(promo.expires_at).split(' ')[0]}</strong>.</>
            ) : null}
          </p>

          <div className="promo-code-row">
            <div className="promo-code" title="Coupon code">
              <span className="promo-code__label">Code</span>
              <span className="promo-code__value">{promo.code}</span>
            </div>
            <button type="button" onClick={onCopy} className="promo-copy-btn" aria-live="polite">
              {copied ? (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden><polyline points="20 6 9 17 4 12" /></svg>
                  Copied
                </>
              ) : (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                  Copy code
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function timeLeft(iso?: string) {
  if (!iso) return null;
  const end = new Date(iso.replace(' ', 'T')).getTime();
  if (Number.isNaN(end)) return null;
  const ms = end - Date.now();
  if (ms <= 0) return null;
  const d = Math.floor(ms / 86_400_000);
  const h = Math.floor((ms % 86_400_000) / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  return { d, h, m };
}
